import { useReviewRecordingReturn } from "./useReviewRecordingReturn";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { errorMessage } from "@/lib/api";
import {
  isCurrentDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { readReviewCarry } from "./reviewState";
import type { MatchSlot } from "@/features/matches/matchSlots";
import type { DrawingV2 } from "@/lib/canvas/telestrator-schema";
export function ReviewRecordingReturn({
  owner,
  captureId,
  matchId,
  slot,
  video,
  drawing,
}: {
  owner: DraftContext | null;
  captureId: string;
  matchId: string;
  slot: MatchSlot;
  video: React.RefObject<HTMLVideoElement>;
  drawing?: DrawingV2;
}) {
  const navigate = useNavigate();
  const carry = owner ? readReviewCarry(owner, { captureId }) : null;
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const mutation = useReviewRecordingReturn(
    owner,
    captureId,
    matchId,
    slot,
    video,
    drawing,
  );
  return (
    <section
      className="flex flex-wrap items-center gap-2 border-t border-rule p-3"
      aria-label="Inbox recording evidence"
    >
      <p className="text-sm">
        {mutation.privateRecording
          ? "Save this moment as an independent Resource, then return to your draft. Feedback stays unpublished."
          : mutation.independentRecording
          ? "Save this moment as an independent Resource, then return to review your writing and recipients. Feedback is not published here."
          : "Choose the real recording and frame. Return to approve the same draft; no extra note or clip is created."}
      </p>
      <Button
        disabled={!carry || !matchId || mutation.isPending}
        onClick={() => {
          if (inFlight.current) return;
          inFlight.current = true;
          setError("");
          void mutation
            .mutateAsync()
            .then(() => {
              if (alive.current && owner && isCurrentDraftContext(owner))
                void navigate({ to: "/coach", search: { tab: "board", inbox_capture_id: captureId } });
            })
            .catch((e) => {
              if (alive.current && owner && isCurrentDraftContext(owner))
                setError(errorMessage(e));
            })
            .finally(() => {
              inFlight.current = false;
            });
        }}
      >
        {mutation.independentRecording ? mutation.rejectedSave ? "Review current frame and retry"
          : mutation.pendingSave ? "Check original save and return"
          : mutation.savedResource ? "Retry return with saved moment" : "Save moment and return"
          : "Use this recording and playhead"}
      </Button>
      <Button
        variant="outline"
        onClick={() =>
          void navigate({ to: "/coach", search: { tab: "board", inbox_capture_id: captureId } })
        }
      >
        Return without attaching
      </Button>
      {error && (
        <p role="alert" className="text-sm">
          {error}
        </p>
      )}
      {mutation.independentRecording && mutation.savedResource && <p role="status" className="text-sm">
        Resource saved independently. Your writing and original return target are kept.
      </p>}
      {mutation.independentRecording && mutation.rejectedSave && <p className="text-sm">
        This save was rejected. Check the selected recording and frame before trying a corrected save.
      </p>}
    </section>
  );
}
